import axios from "axios";
import { toast } from "react-hot-toast";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8001/api/v1";

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30-second timeout
  headers: {
    "Content-Type": "application/json",
  },
  // Enable credentials for HTTPS
  withCredentials: true,
});

// Lightweight client-side request timing and request-id propagation
api.interceptors.request.use((config) => {
  // attach a high-resolution start time
  config.metadata = { start: performance.now() };
  // propagate/assign X-Request-ID
  const reqId = crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
  config.headers["X-Request-ID"] = reqId;
  config.metadata.requestId = reqId;
  return config;
});

api.interceptors.response.use(
  (response) => {
    const start = response.config.metadata?.start || performance.now();
    const totalMs = performance.now() - start;
    const rid = response.headers["x-request-id"] || response.config.metadata?.requestId;
    const serverTiming = response.headers["server-timing"];
    const xResp = response.headers["x-response-time"];
    const xDb = response.headers["x-db-time"];
    const xDbQ = response.headers["x-db-queries"];
    // One-line debug for slow calls (>300ms)
    if (totalMs > 300) {
      console.warn(
        `[API][slow] ${response.config.method?.toUpperCase()} ${response.config.url} total=${totalMs.toFixed(1)}ms reqId=${rid} serverTiming="${serverTiming}" xResp=${xResp} xDb=${xDb} xDbQ=${xDbQ}`
      );
    }
    return response;
  },
  (error) => {
    try {
      const cfg = error.config || {};
      const start = cfg.metadata?.start || performance.now();
      const totalMs = performance.now() - start;
      const rid = error.response?.headers?.["x-request-id"] || cfg.metadata?.requestId;
      const serverTiming = error.response?.headers?.["server-timing"];
      const xResp = error.response?.headers?.["x-response-time"];
      const xDb = error.response?.headers?.["x-db-time"];
      const xDbQ = error.response?.headers?.["x-db-queries"];
      console.error(
        `[API][error] ${cfg.method?.toUpperCase()} ${cfg.url} total=${totalMs.toFixed(1)}ms reqId=${rid} serverTiming="${serverTiming}" xResp=${xResp} xDb=${xDb} xDbQ=${xDbQ}`
      );
    } catch (_) {}
    return Promise.reject(error);
  }
);

// Add a request interceptor to include the token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    console.log("[API Interceptor] Request URL:", config.url);
    console.log("[API Interceptor] Token exists:", !!token);
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
      console.log("[API Interceptor] Authorization header set");
    } else {
      console.log("[API Interceptor] No token found");
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    console.error("[API Interceptor] Response error:", error);
    console.error("[API Interceptor] Error status:", error.response?.status);
    console.error("[API Interceptor] Error data:", error.response?.data);

    const originalRequest = error.config;

    // Skip interceptor redirect for auth endpoints — let the caller handle errors
    const isAuthRequest = originalRequest?.url?.startsWith("/auth/");
    if (isAuthRequest) {
      return Promise.reject(error);
    }

    // If token is expired (401) and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem("refreshToken");
        if (refreshToken) {
          console.log("[API Interceptor] Attempting token refresh...");
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });

          const { access_token } = response.data;
          localStorage.setItem("token", access_token);
          console.log("[API Interceptor] Token refreshed successfully");

          // Retry the original request with new token
          originalRequest.headers["Authorization"] = `Bearer ${access_token}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        console.error("[API Interceptor] Token refresh failed:", refreshError);
        // Clear tokens and redirect to login
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        window.location.href = "/login";
        return Promise.reject(refreshError);
      }
    }

    // If refresh failed or other error, redirect to login
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// Auth service
export const login = async (email, password) => {
  try {
    console.log("[API] Attempting login for:", email);
    console.log("[API] Using base URL:", API_BASE_URL);
    console.log("[API] Request payload:", { email, password: "***" });

    // Test localStorage functionality
    try {
      localStorage.setItem("test", "test");
      const testValue = localStorage.getItem("test");
      localStorage.removeItem("test");
      console.log("[API] localStorage test:", testValue === "test" ? "PASSED" : "FAILED");
    } catch (localStorageError) {
      console.error("[API] localStorage test FAILED:", localStorageError);
    }

    const response = await api.post("/auth/login", { email, password });
    console.log("[API] Login response:", response.data);

    if (response.data.access_token) {
      try {
        localStorage.setItem("token", response.data.access_token);
        localStorage.setItem("refreshToken", response.data.refresh_token);
        console.log("[API] Tokens stored in localStorage successfully");

        // Verify storage
        const storedToken = localStorage.getItem("token");
        console.log("[API] Stored token verification:", storedToken ? "SUCCESS" : "FAILED");
      } catch (storageError) {
        console.error("[API] Failed to store tokens in localStorage:", storageError);
      }
    }
    return response.data;
  } catch (error) {
    // Preserve the original response so callers can check status codes
    throw error;
  }
};

export const logout = async () => {
  try {
    const token = localStorage.getItem("token");
    if (token) {
      await api.post("/auth/logout", { token });
    }
  } catch (error) {
    console.error("[API] Logout error:", error);
  } finally {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
  }
};

export const logoutAllSessions = async () => {
  try {
    await api.post("/auth/logout-all");
  } catch (error) {
    console.error("[API] Logout all sessions error:", error);
  } finally {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
  }
};

export const getActiveSessions = async () => {
  try {
    const response = await api.get("/auth/sessions");
    return response.data;
  } catch (error) {
    console.error("[API] Get active sessions error:", error);
    throw error;
  }
};

export const register = async (userData) => {
  const response = await api.post("/auth/register", userData);
  return response.data;
};

// Email verification
export const verifyEmail = async (token) => {
  const response = await api.get(`/auth/verify-email?token=${encodeURIComponent(token)}`);
  return response.data;
};

export const resendVerification = async (email) => {
  const response = await api.post("/auth/resend-verification", { email });
  return response.data;
};

// System settings (admin)
export const getSystemSettings = async () => {
  const response = await api.get("/settings/");
  return response.data;
};

export const getSystemSettingsByCategory = async (category) => {
  const response = await api.get(`/settings/${category}`);
  return response.data;
};

export const updateSystemSetting = async (key, value) => {
  const response = await api.put(`/settings/${key}`, { value });
  return response.data;
};

export const testEmailSettings = async (toEmail) => {
  const response = await api.post("/settings/test-email", { to_email: toEmail });
  return response.data;
};

// OAuth functions
export const googleOAuth = async (code) => {
  try {
    const response = await api.post("/auth/oauth/google", { code, provider: "google" });
    return response.data;
  } catch (error) {
    console.error("[API] Google OAuth error:", error);
    throw error;
  }
};

export const getProfile = async () => {
  const token = localStorage.getItem("token");
  try {
    console.log("[API] Token for profile request:", !!token);

    const response = await api.get("/auth/me");
    return response.data;
  } catch (error) {
    console.error("[API] getProfile error:", error);
    console.error("[API] getProfile error response:", error.response?.data);
    console.error("[API] getProfile error status:", error.response?.status);
    throw error;
  }
};

// Exchange functions
export const getExchanges = async () => {
  console.log("[API] Fetching exchanges...");
  const response = await api.get("/exchanges");
  console.log("[API] Exchanges response:", response.data);
  return response.data;
};

export const getAvailableExchanges = async () => {
  const response = await api.get("/exchanges/available");
  return response.data;
};

export const getExchange = async (id) => {
  const response = await api.get(`/exchanges/${id}`);
  return response.data;
};

export const createExchange = async (exchangeData) => {
  const response = await api.post("/exchanges", exchangeData);
  return response.data;
};

export const updateExchange = async (id, exchangeData) => {
  const response = await api.put(`/exchanges/${id}`, exchangeData);
  return response.data;
};

export const deleteExchange = async (id) => {
  const response = await api.delete(`/exchanges/${id}`);
  return response.data;
};

export const toggleExchangeActive = async (exchangeId) => {
  const response = await api.post(`/exchanges/${exchangeId}/toggle-active`);
  return response.data;
};

// Strategy functions
export const getStrategies = async () => {
  const response = await api.get("/strategies");
  return response.data;
};

export const getStrategy = async (id) => {
  try {
    const response = await api.get(`/strategies/${id}`);
    return response.data;
  } catch (error) {
    console.error("Get strategy error:", error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || "Failed to fetch strategy");
  }
};

export const createStrategy = async (strategyData) => {
  try {
    const formattedData = {
      name: strategyData.name,
      description: strategyData.description,
      indicator_groups: strategyData.indicator_groups || [],
      group_logic: strategyData.group_logic || "AND",
      exit_indicator_groups: strategyData.exit_indicator_groups || [],
      exit_group_logic: strategyData.exit_group_logic || "OR",
      logic: strategyData.logic || "AND",
      indicators: strategyData.indicators || [],
      is_public: strategyData.is_public,
    };
    const response = await api.post("/strategies", formattedData);
    return response.data;
  } catch (error) {
    console.error("Create strategy error:", error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || "Failed to create strategy");
  }
};

export const updateStrategy = async (strategyId, strategyData) => {
  try {
    const formattedData = {
      name: strategyData.name,
      description: strategyData.description,
      indicator_groups: strategyData.indicator_groups || [],
      group_logic: strategyData.group_logic || "AND",
      exit_indicator_groups: strategyData.exit_indicator_groups || [],
      exit_group_logic: strategyData.exit_group_logic || "OR",
      logic: strategyData.logic || "AND",
      indicators: strategyData.indicators || [],
      is_public: strategyData.is_public,
    };
    const response = await api.put(`/strategies/${strategyId}`, formattedData);
    return response.data;
  } catch (error) {
    console.error("Update strategy error:", error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || "Failed to update strategy");
  }
};

export const deleteStrategy = async (strategyId) => {
  try {
    const response = await api.delete(`/strategies/${strategyId}`);
    return response.data;
  } catch (error) {
    console.error("Delete strategy error:", error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || "Failed to delete strategy");
  }
};

export const toggleStrategyPublic = async (strategyId) => {
  try {
    const response = await api.post(`/strategies/admin/${strategyId}/toggle-public`);
    return response.data;
  } catch (error) {
    console.error("Toggle strategy public error:", error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || "Failed to toggle strategy visibility");
  }
};

export const getStrategyPerformance = async (strategyId) => {
  try {
    const response = await api.get(`/strategies/${strategyId}/performance`);
    return response.data;
  } catch (error) {
    console.error("[API] Get strategy performance error:", error);
    throw error;
  }
};

export const getBotsUsingStrategy = async (strategyId) => {
  try {
    const response = await api.get(`/strategies/${strategyId}/bots`);
    return response.data;
  } catch (error) {
    console.error("[API] Get bots using strategy error:", error);
    throw error;
  }
};

export const restartBotsUsingStrategy = async (strategyId) => {
  try {
    const response = await api.post(`/strategies/${strategyId}/restart-bots`);
    return response.data;
  } catch (error) {
    console.error("[API] Restart bots using strategy error:", error);
    throw error;
  }
};

// User management functions
export const list = async () => {
  const response = await api.get("/admin/users");
  return response.data;
};

export const create = async (userData) => {
  const response = await api.post("/admin/users", userData);
  return response.data;
};

export const update = async (id, userData) => {
  const response = await api.put(`/admin/users/${id}`, userData);
  return response.data;
};

export const deleteUser = async (id) => {
  try {
    console.log("Deleting user with ID:", id);
    const response = await api.delete(`/admin/users/${id}`);
    console.log("Delete response:", response);
    return response.data;
  } catch (error) {
    console.error("Delete user error:", error.response?.data || error.message);
    throw error;
  }
};

export const toggleAdmin = async (id) => {
  const response = await api.post(`/admin/users/${id}/toggle-admin`);
  return response.data;
};

// Admin system health API (Phase 6 & 7)
export const adminHealthApi = {
  getSystemHealth: async () => {
    const response = await api.get("/admin/health");
    return response.data;
  },
  getCircuits: async () => {
    const response = await api.get("/admin/circuits");
    return response.data;
  },
  forceOpenCircuit: async (exchange) => {
    const response = await api.post(`/admin/circuits/${exchange}/force-open`);
    return response.data;
  },
  forceCloseCircuit: async (exchange) => {
    const response = await api.post(`/admin/circuits/${exchange}/force-close`);
    return response.data;
  },
  getBotsHealth: async () => {
    const response = await api.get("/admin/bots/health");
    return response.data;
  },
  getBotHealth: async (botId) => {
    const response = await api.get(`/admin/bots/${botId}/health`);
    return response.data;
  },
  restartBot: async (botId) => {
    const response = await api.post(`/admin/bots/${botId}/restart`);
    return response.data;
  },
};

export const tradingApi = {
  // Market Data
  getMarketData: async (symbol, exchangeId = null) => {
    const formattedSymbol = symbol.replace("/", "");
    const params = exchangeId ? `?exchange_id=${exchangeId}` : "";
    const response = await api.get(`/market-data/${formattedSymbol}${params}`);
    return response.data;
  },
  getOrderBook: async (symbol) => {
    const response = await api.get(`/market-data/${symbol}/orderbook`);
    return response.data;
  },
  getExchanges: async () => {
    const response = await api.get("/exchanges");
    return response.data;
  },
  getAvailableExchanges: async () => {
    const response = await api.get("/exchanges/available");
    return response.data;
  },
  getStrategies: async () => {
    const response = await api.get("/strategies");
    return response.data;
  },

  // Trading Data
  getPositions: async (exchangeId = null) => {
    const params = exchangeId ? `?exchange_id=${exchangeId}` : "";
    const response = await api.get(`/positions${params}`);
    return response.data;
  },
  getOrders: async (exchangeId = null) => {
    const params = exchangeId ? `?exchange_id=${exchangeId}` : "";
    const response = await api.get(`/orders${params}`);
    return response.data;
  },
  getBalance: async (exchangeId = null, connectionId = null) => {
    const params = {};
    if (exchangeId) params.exchange_id = exchangeId;
    if (connectionId) params.connection_id = connectionId;
    const response = await api.get("/balance", { params });
    return response.data;
  },

  // Order Management
  placeOrder: async (orderData, exchangeId = null) => {
    const params = exchangeId ? `?exchange_id=${exchangeId}` : "";
    const response = await api.post(`/orders${params}`, orderData);
    return response.data;
  },
  cancelOrder: async (orderId, symbol, exchangeId = null) => {
    const params = exchangeId ? `&exchange_id=${exchangeId}` : "";
    const response = await api.delete(`/orders/${orderId}?symbol=${symbol}${params}`);
    return response.data;
  },
  getOrder: async (orderId, symbol, exchangeId = null) => {
    const params = exchangeId ? `&exchange_id=${exchangeId}` : "";
    const response = await api.get(`/orders/${orderId}?symbol=${symbol}${params}`);
    return response.data;
  },
  getOrderHistory: async (exchangeId = null) => {
    const params = exchangeId ? `?exchange_id=${exchangeId}` : "";
    const response = await api.get(`/orders/history${params}`);
    return response.data;
  },

  // Symbols
  getSymbols: async (exchangeId = null) => {
    const params = exchangeId ? `?exchange_id=${exchangeId}` : "";
    const response = await api.get(`/symbols${params}`);
    return response.data;
  },
};

export const getRiskPresets = async () => {
  try {
    const response = await api.get("/risk/presets");
    return response.data;
  } catch (error) {
    console.error("[API] getRiskPresets error:", error);
    return {};
  }
};

export const getRiskConfigs = async () => {
  try {
    const response = await api.get("/risk/configs");
    const configs = response.data;

    console.log("[API] getRiskConfigs response:", configs);

    // Return the array of risk configs directly
    return Array.isArray(configs) ? configs : [];
  } catch (error) {
    console.error("[API] getRiskConfigs error:", error);
    return [];
  }
};

export const botApi = {
  // Bot Management
  getBots: async () => {
    try {
      console.log("[API] Getting bots...");
      const token = localStorage.getItem("token");
      console.log("[API] Token for bots request:", !!token);

      const response = await api.get("/bots");
      console.log("[API] Bots response:", response.data);
      return response.data;
    } catch (error) {
      console.error("[API] getBots error:", error);
      console.error("[API] getBots error response:", error.response?.data);
      console.error("[API] getBots error status:", error.response?.status);
      throw error;
    }
  },
  getBot: async (botId, options = {}) => {
    const response = await api.get(`/bots/${botId}`, options);
    return response.data;
  },
  createBot: async (botData) => {
    const response = await api.post("/bots", botData);
    return response.data;
  },
  updateBot: async (botId, botData) => {
    const response = await api.put(`/bots/${botId}`, botData);
    return response.data;
  },
  deleteBot: async (botId) => {
    const response = await api.delete(`/bots/${botId}`);
    return response.data;
  },
  startBot: async (botId) => {
    const response = await api.post(`/bots/${botId}/start`);
    return response.data;
  },
  stopBot: async (botId, { closePositions = false } = {}) => {
    const response = await api.post(`/bots/${botId}/stop`, null, {
      params: closePositions ? { close_positions: true } : {},
    });
    return response.data;
  },

  // Bot Strategies
  getBotStrategies: async (botId, options = {}) => {
    const response = await api.get(`/bots/${botId}/strategies`, options);
    return response.data;
  },
  addStrategyToBot: async (botId, strategyData) => {
    const response = await api.post(`/bots/${botId}/strategies`, strategyData);
    return response.data;
  },
  removeStrategyFromBot: async (botId, strategyId) => {
    const response = await api.delete(`/bots/${botId}/strategies/${strategyId}`);
    return response.data;
  },

  // Bot Trades
  getBotTrades: async (botId, limit = 100) => {
    const response = await api.get(`/bots/${botId}/trades?limit=${limit}`);
    return response.data;
  },

  // Test Notification
  testNotification: async (botId) => {
    try {
      const response = await api.post(`/bots/${botId}/test-notification`);
      return response.data;
    } catch (error) {
      console.error("[API] testNotification error:", error);
      throw error;
    }
  },

  // Test Signal Notification
  testSignalNotification: async (botId) => {
    try {
      const response = await api.post(`/bots/${botId}/test-signal-notification`);
      return response.data;
    } catch (error) {
      console.error("[API] testSignalNotification error:", error);
      throw error;
    }
  },

  // Test SELL Signal Notification
  testSellSignalNotification: async (botId) => {
    try {
      const response = await api.post(`/bots/${botId}/test-sell-signal`);
      return response.data;
    } catch (error) {
      console.error("[API] testSellSignalNotification error:", error);
      throw error;
    }
  },

  // Test HOLD Signal Notification
  testHoldSignalNotification: async (botId) => {
    try {
      const response = await api.post(`/bots/${botId}/test-hold-signal`);
      return response.data;
    } catch (error) {
      console.error("[API] testHoldSignalNotification error:", error);
      throw error;
    }
  },
};

// Exchange Symbols API
export const getExchangeSymbols = async (exchangeId, tradingType = "spot", baseCurrency = null) => {
  try {
    let url = `/exchange-symbols/exchanges/${exchangeId}/symbols?trading_type=${tradingType}`;
    if (baseCurrency) {
      url += `&base_currency=${baseCurrency}`;
    }
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error("[API] getExchangeSymbols error:", error);
    return [];
  }
};

export const validateSymbols = async (symbols, exchangeId) => {
  try {
    const response = await api.post("/exchange-symbols/validate-symbols", {
      symbols: symbols,
      exchange_id: exchangeId,
    });
    return response.data;
  } catch (error) {
    console.error("[API] validateSymbols error:", error);
    return { valid: [], invalid: symbols, total_requested: symbols.length, total_valid: 0 };
  }
};

// Admin Exchange Symbols API
export const adminSymbolApi = {
  getAllSymbols: async (filters = {}, page = 1, pageSize = 50) => {
    try {
      const params = new URLSearchParams();
      if (filters.exchange_id) params.append("exchange_id", filters.exchange_id);
      if (filters.trading_type) params.append("trading_type", filters.trading_type);
      if (filters.is_active !== undefined) params.append("is_active", filters.is_active);
      params.append("page", page);
      params.append("page_size", pageSize);

      const url = `/exchange-symbols/admin?${params.toString()}`;
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error("[API] getAllSymbols error:", error);
      throw error;
    }
  },

  addSymbol: async (symbolData) => {
    try {
      const response = await api.post("/exchange-symbols/admin", symbolData);
      return response.data;
    } catch (error) {
      console.error("[API] addSymbol error:", error);
      throw error;
    }
  },

  updateSymbol: async (symbolId, symbolData) => {
    try {
      const response = await api.put(`/exchange-symbols/admin/${symbolId}`, symbolData);
      return response.data;
    } catch (error) {
      console.error("[API] updateSymbol error:", error);
      throw error;
    }
  },

  deleteSymbol: async (symbolId) => {
    try {
      const response = await api.delete(`/exchange-symbols/admin/${symbolId}`);
      return response.data;
    } catch (error) {
      console.error("[API] deleteSymbol error:", error);
      throw error;
    }
  },

  syncSymbols: async (exchangeId) => {
    try {
      const response = await api.post(`/exchange-symbols/admin/sync/${exchangeId}`);
      return response.data;
    } catch (error) {
      console.error("[API] syncSymbols error:", error);
      throw error;
    }
  },
};

// AI Pre-Training API (Admin)
export const adminPretrainApi = {
  getStatus: async (params = {}) => {
    try {
      const query = new URLSearchParams();
      if (params.exchange_id) query.append("exchange_id", params.exchange_id);
      if (params.status_filter) query.append("status_filter", params.status_filter);
      if (params.timeframe_filter) query.append("timeframe_filter", params.timeframe_filter);
      if (params.page) query.append("page", params.page);
      if (params.page_size) query.append("page_size", params.page_size);
      const response = await api.get(`/admin/ai-pretrain/status?${query.toString()}`);
      return response.data;
    } catch (error) {
      console.error("[API] getPretrainStatus error:", error);
      throw error;
    }
  },
  triggerFullRetrain: async () => {
    try {
      const response = await api.post("/admin/ai-pretrain/trigger");
      return response.data;
    } catch (error) {
      console.error("[API] triggerFullRetrain error:", error);
      throw error;
    }
  },
  scanAndQueue: async () => {
    try {
      const response = await api.post("/admin/ai-pretrain/scan");
      return response.data;
    } catch (error) {
      console.error("[API] scanAndQueue error:", error);
      throw error;
    }
  },
  pauseTraining: async () => {
    try {
      const response = await api.post("/admin/ai-pretrain/pause");
      return response.data;
    } catch (error) {
      console.error("[API] pauseTraining error:", error);
      throw error;
    }
  },
  resumeTraining: async () => {
    try {
      const response = await api.post("/admin/ai-pretrain/resume");
      return response.data;
    } catch (error) {
      console.error("[API] resumeTraining error:", error);
      throw error;
    }
  },
  trainSymbol: async (exchangeId, symbol, timeframe = "1h") => {
    try {
      const params = new URLSearchParams({ exchange_id: exchangeId, symbol, timeframe });
      const response = await api.post(`/admin/ai-pretrain/train-symbol?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error("[API] trainSymbol error:", error);
      throw error;
    }
  },
};

// Backtesting API
export const backtestApi = {
  startBacktest: async (config) => {
    try {
      const response = await api.post("/backtest/start", config);
      return response.data;
    } catch (error) {
      console.error("[API] startBacktest error:", error);
      throw error;
    }
  },

  getBacktestStatus: async (jobId) => {
    try {
      const response = await api.get(`/backtest/status/${jobId}`);
      return response.data;
    } catch (error) {
      console.error("[API] getBacktestStatus error:", error);
      throw error;
    }
  },

  getBacktestResults: async (jobId) => {
    try {
      const response = await api.get(`/backtest/results/${jobId}`);
      return response.data;
    } catch (error) {
      console.error("[API] getBacktestResults error:", error);
      throw error;
    }
  },

  stopBacktest: async (jobId) => {
    try {
      const response = await api.post(`/backtest/stop/${jobId}`);
      return response.data;
    } catch (error) {
      console.error("[API] stopBacktest error:", error);
      throw error;
    }
  },

  getSavedBacktests: async () => {
    try {
      const response = await api.get("/backtest/saved");
      return response.data;
    } catch (error) {
      console.error("[API] getSavedBacktests error:", error);
      return [];
    }
  },

  saveBacktestConfig: async (configData) => {
    try {
      const response = await api.post("/backtest/save-config", configData);
      return response.data;
    } catch (error) {
      console.error("[API] saveBacktestConfig error:", error);
      throw error;
    }
  },

  deleteSavedConfig: async (configId) => {
    try {
      const response = await api.delete(`/backtest/saved/${configId}`);
      return response.data;
    } catch (error) {
      console.error("[API] deleteSavedConfig error:", error);
      throw error;
    }
  },

  // Health check
  healthCheck: async () => {
    try {
      const response = await api.get("/backtest/health");
      return response.data;
    } catch (error) {
      console.error("[API] backtest health check error:", error);
      throw error;
    }
  },

  updateStrategy: async (strategyId, strategyData) => {
    try {
      // Convert the strategy data to the new multi-indicator format
      const formattedData = {
        name: strategyData.name,
        description: strategyData.description,
        indicators: strategyData.indicators || [],
        logic: strategyData.logic || "AND",
        is_public: strategyData.is_public,
      };
      const response = await api.put(`/strategies/${strategyId}`, formattedData);
      return response.data;
    } catch (error) {
      console.error("[BacktestApi] Update strategy error:", error.response?.data || error.message);
      throw new Error(error.response?.data?.detail || "Failed to update strategy");
    }
  },
};

// Secure Exchange Connections API
export const secureConnectionApi = {
  getAllConnections: async () => {
    try {
      // NOTE: Backend routes are defined with a trailing slash (FastAPI will 307 redirect otherwise).
      // Some clients may drop Authorization headers on redirect; avoid redirect entirely.
      const response = await api.get("/user-exchange-connections/");
      return response.data;
    } catch (error) {
      console.error("[API] getAllConnections error:", error);
      throw error;
    }
  },

  getAllConnectionsAdmin: async () => {
    try {
      const response = await api.get("/user-exchange-connections/admin/all");
      return response.data;
    } catch (error) {
      console.error("[API] getAllConnectionsAdmin error:", error);
      throw error;
    }
  },

  getConnection: async (connectionId) => {
    try {
      const response = await api.get(`/user-exchange-connections/${connectionId}`);
      return response.data;
    } catch (error) {
      console.error("[API] getConnection error:", error);
      throw error;
    }
  },

  createConnection: async (connectionData) => {
    try {
      // NOTE: Use trailing slash to avoid 307 redirect (see getAllConnections above)
      const response = await api.post("/user-exchange-connections/", connectionData);
      return response.data;
    } catch (error) {
      console.error("[API] createConnection error:", error);
      throw error;
    }
  },

  updateConnection: async (connectionId, connectionData) => {
    try {
      const response = await api.put(`/user-exchange-connections/${connectionId}`, connectionData);
      return response.data;
    } catch (error) {
      console.error("[API] updateConnection error:", error);
      throw error;
    }
  },

  deleteConnection: async (connectionId) => {
    try {
      const response = await api.delete(`/user-exchange-connections/${connectionId}`);
      return response.data;
    } catch (error) {
      console.error("[API] deleteConnection error:", error);
      throw error;
    }
  },

  testConnection: async (connectionId) => {
    try {
      const response = await api.post(`/user-exchange-connections/${connectionId}/test`);
      return response.data;
    } catch (error) {
      console.error("[API] testConnection error:", error);
      throw error;
    }
  },

  getConnectionCredentials: async (connectionId) => {
    try {
      const response = await api.get(`/user-exchange-connections/${connectionId}/credentials`);
      return response.data;
    } catch (error) {
      console.error("[API] getConnectionCredentials error:", error);
      throw error;
    }
  },

  getConnectionUsage: async (exchangeId) => {
    try {
      const response = await api.get(`/user-exchange-connections/usage/${exchangeId}`);
      return response.data;
    } catch (error) {
      console.error("[API] getConnectionUsage error:", error);
      throw error;
    }
  },
};

export const notificationApi = {
  getChannels: async () => {
    const response = await api.get("/notifications/channels");
    return response.data;
  },
  getPreferences: async () => {
    const response = await api.get("/notifications/preferences");
    return response.data;
  },
  createChannel: async (channelData) => {
    const response = await api.post("/notifications/channels", channelData);
    return response.data;
  },
  updateChannel: async (channelId, channelData) => {
    const response = await api.put(`/notifications/channels/${channelId}`, channelData);
    return response.data;
  },
  deleteChannel: async (channelId) => {
    const response = await api.delete(`/notifications/channels/${channelId}`);
    return response.data;
  },
  testChannel: async (channelId) => {
    const response = await api.post(`/notifications/channels/${channelId}/test`);
    return response.data;
  },
  // Add more methods as needed
};

// Indicator functions
export const getIndicators = async () => {
  const response = await api.get("/indicators");
  return response.data;
};

export const getIndicatorDetails = async (indicatorName) => {
  const response = await api.get(`/indicators/${indicatorName}`);
  return response.data;
};

// Analytics API
export const analyticsApi = {
  getFunds: async (botStatus = "all", connectionId = null) => {
    const params = {};
    if (botStatus && botStatus !== "all") params.bot_status = botStatus;
    if (connectionId) params.connection_id = connectionId;
    const response = await api.get("/analytics/funds", { params });
    return response.data;
  },
  getBotPerformance: async (sinceDays = 30, botStatus = "all", connectionId = null) => {
    const params = { since_days: sinceDays };
    if (botStatus && botStatus !== "all") params.bot_status = botStatus;
    if (connectionId) params.connection_id = connectionId;
    const response = await api.get("/analytics/bot-performance", { params });
    return response.data;
  },
  getCoinPerformance: async (sinceDays = 30, botStatus = "all", connectionId = null) => {
    const params = { since_days: sinceDays };
    if (botStatus && botStatus !== "all") params.bot_status = botStatus;
    if (connectionId) params.connection_id = connectionId;
    const response = await api.get("/analytics/coin-performance", { params });
    return response.data;
  },
};

// Bot Detail Page API Methods
export const botDetailApi = {
  // Get dashboard statistics
  getDashboardStats: async (botStatus = "all", connectionId = null) => {
    try {
      const params = {};
      if (botStatus && botStatus !== "all") params.bot_status = botStatus;
      if (connectionId) params.connection_id = connectionId;
      const response = await api.get("/bots/dashboard-stats", { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching dashboard statistics:", error);
      throw error;
    }
  },

  // Get bot performance metrics and charts data
  getBotPerformance: async (botId, options = {}) => {
    try {
      const response = await api.get(`/bots/${botId}/performance`, options);
      return response.data;
    } catch (error) {
      console.error("Error fetching bot performance:", error);
      throw error;
    }
  },

  // Get bot trade history with pagination
  getBotTrades: async (botId, page = 1, limit = 50, options = {}) => {
    try {
      const response = await api.get(`/bots/${botId}/trades`, {
        params: { page, limit },
        ...options,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching bot trades:", error);
      throw error;
    }
  },

  // Get bot active positions
  getBotPositions: async (botId, options = {}) => {
    try {
      const response = await api.get(`/bots/${botId}/positions`, options);
      return response.data;
    } catch (error) {
      console.error("Error fetching bot positions:", error);
      throw error;
    }
  },

  // Update current prices for all open positions (server fetches live ticker prices via CCXT)
  updateBotPositionPrices: async (botId, options = {}) => {
    try {
      const response = await api.post(`/bots/${botId}/positions/update-prices`, null, options);
      return response.data;
    } catch (error) {
      console.error("Error updating bot position prices:", error);
      throw error;
    }
  },

  // Get bot completed trades with pagination
  getBotCompletedTrades: async (botId, page = 1, limit = 50, options = {}) => {
    try {
      const response = await api.get(`/bots/${botId}/completed-trades`, {
        params: { page, limit },
        ...options,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching bot completed trades:", error);
      throw error;
    }
  },

  // Export bot data
  exportBotData: async (botId, exportRequest) => {
    try {
      const response = await api.post(`/bots/${botId}/export`, exportRequest);
      return response.data;
    } catch (error) {
      console.error("Error exporting bot data:", error);
      throw error;
    }
  },

  // Close a specific position by ID
  closePosition: async (botId, positionId) => {
    try {
      const response = await api.post(`/bots/${botId}/positions/${positionId}/close`);
      return response.data;
    } catch (error) {
      console.error("Error closing position:", error);
      throw error;
    }
  },

  // Close multiple positions by IDs
  closeBulkPositions: async (botId, positionIds) => {
    try {
      const response = await api.post(`/bots/${botId}/positions/bulk-close`, positionIds);
      return response.data;
    } catch (error) {
      console.error("Error closing bulk positions:", error);
      throw error;
    }
  },

  // Close all positions for a bot
  closeAllPositions: async (botId) => {
    try {
      const response = await api.post(`/bots/${botId}/positions/close-all`);
      return response.data;
    } catch (error) {
      console.error("Error closing all positions:", error);
      throw error;
    }
  },

  // Legacy method for backward compatibility (symbol-based)
  closePositionBySymbol: async (botId, positionRequest) => {
    try {
      const response = await api.post(`/bots/${botId}/close-position`, positionRequest);
      return response.data;
    } catch (error) {
      console.error("Error closing position:", error);
      throw error;
    }
  },
};

// Fetch trade pairs for a bot (with pagination)
export const getBotTradePairs = async (botId, page = 1, limit = 100) => {
  try {
    const response = await api.get(`/bots/${botId}/trade-pairs?page=${page}&limit=${limit}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching bot trade pairs:", error);
    throw error;
  }
};

export const getBotTradePairSummary = async (botId, options = {}) => {
  const response = await api.get(`/bots/${botId}/trade-pairs/summary`, options);
  return response.data;
};

// AI Premium API
export const aiApi = {
  // Strategy Builder
  generateStrategy: async (description, name = null) => {
    const response = await api.post("/ai/strategy/generate", { description, name });
    return response.data;
  },
  explainStrategy: async (strategyConfig) => {
    const response = await api.post("/ai/strategy/explain", { strategy_config: strategyConfig });
    return response.data;
  },

  // Indicator Intelligence
  getRecommendation: async (symbol, timeframe = "1h", style = "balanced") => {
    const base = symbol.split("/")[0].toUpperCase();
    const response = await api.get(`/ai/recommend/${encodeURIComponent(base)}`, {
      params: { timeframe, style },
    });
    return response.data;
  },
  applyRecommendation: async (strategyConfig) => {
    const response = await api.post("/ai/recommend/apply", { strategy_config: strategyConfig });
    return response.data;
  },

  // Sentiment
  getSentiment: async (symbol) => {
    const response = await api.get(`/ai/sentiment/${encodeURIComponent(symbol)}`);
    return response.data;
  },
  getSentimentOverview: async () => {
    const response = await api.get("/ai/sentiment/overview");
    return response.data;
  },

  // Market Regime
  getRegime: async (symbol, timeframe = "1d") => {
    const base = symbol.split("/")[0].toUpperCase();
    const response = await api.get(`/ai/regime/${encodeURIComponent(base)}`, {
      params: { timeframe },
    });
    return response.data;
  },
  trainRegime: async (symbol, timeframe = "1d") => {
    const response = await api.post("/ai/regime/train", { symbol, timeframe });
    return response.data;
  },

  // AI Status & Settings (admin)
  getStatus: async () => {
    const response = await api.get("/ai/status");
    return response.data;
  },
  getSettings: async () => {
    const response = await api.get("/ai/settings");
    return response.data;
  },
  updateSettings: async (settings) => {
    const response = await api.put("/ai/settings", settings);
    return response.data;
  },
};

// ── AI Bot API ──────────────────────────────────────────────────────────────
export const aiBotApi = {
  create: async (config) => {
    const response = await api.post("/ai-bot/", config);
    return response.data;
  },
  getTopSymbols: async (exchangeId, timeframe = "1h", maxSymbols = 10, minAccuracy = 0.70) => {
    const response = await api.get("/ai-bot/top-symbols", {
      params: { exchange_id: exchangeId, timeframe, max_symbols: maxSymbols, min_accuracy: minAccuracy },
    });
    return response.data;
  },
  list: async () => {
    const response = await api.get("/ai-bot/");
    return response.data;
  },
  get: async (botId) => {
    const response = await api.get(`/ai-bot/${botId}`);
    return response.data;
  },
  update: async (botId, data) => {
    const response = await api.patch(`/ai-bot/${botId}`, data);
    return response.data;
  },
  start: async (botId) => {
    const response = await api.post(`/ai-bot/${botId}/start`);
    return response.data;
  },
  stop: async (botId) => {
    const response = await api.post(`/ai-bot/${botId}/stop`);
    return response.data;
  },
  delete: async (botId) => {
    const response = await api.delete(`/ai-bot/${botId}`);
    return response.data;
  },
  getPredictions: async (botId, page = 1, limit = 50) => {
    const response = await api.get(`/ai-bot/${botId}/predictions`, { params: { page, limit } });
    return response.data;
  },
  startBacktest: async (payload) => {
    const response = await api.post("/ai-bot/backtest", payload);
    return response.data;
  },
  getBacktest: async (jobId) => {
    const response = await api.get(`/ai-bot/backtest/${jobId}`);
    return response.data;
  },
  getModelStatus: async (botId) => {
    const response = await api.get(`/ai-bot/${botId}/model-status`);
    return response.data;
  },
  retrain: async (botId) => {
    const response = await api.post(`/ai-bot/${botId}/retrain`);
    return response.data;
  },
  getTrades: async (botId, page = 1, limit = 50) => {
    const response = await api.get(`/bots/${botId}/trades`, { params: { page, limit } });
    return response.data;
  },
};

// ── Arbitrage Bot API ──────────────────────────────────────────────────────
export const arbitrageBotApi = {
  create: async (config) => {
    const response = await api.post("/arbitrage-bot/", config);
    return response.data;
  },
  list: async (arbitrageType = null) => {
    const params = arbitrageType ? { arbitrage_type: arbitrageType } : {};
    const response = await api.get("/arbitrage-bot/", { params });
    return response.data;
  },
  get: async (botId) => {
    const response = await api.get(`/arbitrage-bot/${botId}`);
    return response.data;
  },
  update: async (botId, data) => {
    const response = await api.put(`/arbitrage-bot/${botId}`, data);
    return response.data;
  },
  start: async (botId) => {
    const response = await api.post(`/arbitrage-bot/${botId}/start`);
    return response.data;
  },
  stop: async (botId) => {
    const response = await api.post(`/arbitrage-bot/${botId}/stop`);
    return response.data;
  },
  delete: async (botId) => {
    const response = await api.delete(`/arbitrage-bot/${botId}`);
    return response.data;
  },
  getOpportunities: async (botId, { status = null, page = 1, limit = 20 } = {}) => {
    const params = { page, limit };
    if (status) params.status = status;
    const response = await api.get(`/arbitrage-bot/${botId}/opportunities`, { params });
    return response.data;
  },
  getPairedTrades: async (botId, { page = 1, limit = 20 } = {}) => {
    const response = await api.get(`/arbitrage-bot/${botId}/paired-trades`, { params: { page, limit } });
    return response.data;
  },
  getStats: async (botId) => {
    const response = await api.get(`/arbitrage-bot/${botId}/stats`);
    return response.data;
  },
  scanSpreads: async (connectionIds, symbols) => {
    const response = await api.post("/arbitrage-bot/scan-spreads", {
      connection_ids: connectionIds,
      symbols,
    });
    return response.data;
  },
  fetchSymbols: async (connectionIds) => {
    const response = await api.post("/arbitrage-bot/fetch-symbols", connectionIds);
    return response.data;
  },
  scanTriangles: async (connectionId, baseCurrencies = ["BTC", "ETH", "USDT"]) => {
    const response = await api.post("/arbitrage-bot/scan-triangles", {
      connection_id: connectionId,
      base_currencies: baseCurrencies,
    });
    return response.data;
  },
};

// ── Subscription API ──
export const subscriptionApi = {
  getPlans: async () => {
    const response = await api.get("/subscription/plans");
    return response.data;
  },
  getCurrent: async () => {
    const response = await api.get("/subscription/current");
    return response.data;
  },
  createCheckout: async (tier, billingCycle = "monthly") => {
    const response = await api.post("/subscription/checkout", {
      tier,
      billing_cycle: billingCycle,
    });
    return response.data;
  },
  createCryptoCheckout: async (tier, billingCycle = "monthly", currency = "btc") => {
    const response = await api.post("/subscription/checkout/crypto", {
      tier,
      billing_cycle: billingCycle,
      currency,
    });
    return response.data;
  },
  cancel: async () => {
    const response = await api.post("/subscription/cancel");
    return response.data;
  },
  getPortalUrl: async () => {
    const response = await api.post("/subscription/portal");
    return response.data;
  },
  getBillingHistory: async () => {
    const response = await api.get("/subscription/billing-history");
    return response.data;
  },
};

export default api;
